import type { AppState } from '../../types';

type AppStateInput = {
  // An artifact the chat edits already exists, so there is always something
  // to show: its results never start empty or wait on a job.
  hasArtifact?: boolean;
  isCreatingJob: boolean;
  hasCompletedFirstJob: boolean;
  isChatJobActive: boolean;
  // A chat turn is in flight, whether or not it has started a job yet.
  isChatResponding: boolean;
  hasStartedChatJob: boolean;
  hasStartedChatTurn: boolean;
  hasPendingChatJob: boolean;
  isFetchingChat: boolean;
  eventCount: number;
};

export const computeAppState = ({
  hasArtifact = false,
  isCreatingJob,
  hasCompletedFirstJob,
  isChatJobActive,
  isChatResponding,
  hasStartedChatJob,
  hasStartedChatTurn,
  hasPendingChatJob,
  isFetchingChat,
  eventCount,
}: AppStateInput): AppState => {
  if (hasArtifact) {
    return 'edit';
  }
  if (
    (isCreatingJob && !hasCompletedFirstJob) ||
    // `isChatJobActive` tracks the live AI-SDK stream, which dies when the
    // tab is backgrounded even though the job keeps running server-side.
    // `hasPendingChatJob` (the job history's last job is still non-terminal)
    // covers that window; it's gated on `hasStartedChatJob` so surfaces that
    // merely mount an existing chat with a pending job keep their behavior.
    ((isChatJobActive || (hasStartedChatJob && hasPendingChatJob)) &&
      eventCount === 0 &&
      !hasCompletedFirstJob) ||
    // v2: when a create-* tool yields its terminal chunk, the chat invalidates
    // the getChat query *before* `chatJobActivity.isActive` flips false (hook
    // order in usePromptChat: useToolRefreshSignals above useChatJobActivity),
    // so the chat fetch is already in flight when activity ends. Hold 'loading'
    // through that gap — the empty state should only show once a job has
    // finished, no events exist, and no events request is pending.
    (hasStartedChatJob && eventCount === 0 && isFetchingChat) ||
    // A submitted turn hasn't said yet whether it runs a job: the create tool
    // arrives some way into the stream. Showing results then flashes their
    // empty state before the job's loading card replaces it, so a turn still
    // in flight with nothing to show is loading too.
    (isChatResponding && eventCount === 0 && !hasCompletedFirstJob)
  ) {
    return 'loading';
  }
  // `hasStartedChatTurn` only reaches 'edit', never 'loading': a plain text
  // reply has no job to wait on, so once it settles the results column opens
  // on its empty state.
  if (
    eventCount > 0 ||
    hasCompletedFirstJob ||
    hasStartedChatJob ||
    hasStartedChatTurn
  ) {
    return 'edit';
  }
  return 'default';
};
